import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, Download, Plus, Minus, RefreshCw, History, ArrowRightLeft } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import { useWarehouses } from "../lib/useWarehouses";
import { WarehouseTag } from "../components/WarehouseSelect";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export function SampleQtyBadge({ qty, testId }) {
  const n = Number(qty) || 0;
  const cls = n > 0 ? "bg-brand-50 text-brand-700 border-brand-200" : n < 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-gray-50 text-gray-400 border-gray-200";
  return <span data-testid={testId} className={`inline-block min-w-[2rem] text-center font-mono text-xs px-1.5 py-0.5 rounded border ${cls}`}>{n}</span>;
}

const TYPE_KEYS = ["scan_in", "adjust", "quote_out", "quote_return"];
const Th = ({ children, right }) => <th className={`text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, cls = "" }) => <td className={`px-3 py-2 text-xs text-gray-700 ${cls}`}>{children ?? "-"}</td>;

function AdjustDialog({ target, onClose, onDone, t, warehouseId }) {
  const [mode, setMode] = useState("delta");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setMode("delta"); setValue(""); setNote(""); }, [target]);
  if (!target) return null;
  const submit = async () => {
    if (!note.trim()) { toast.error(t("samples.note_required")); return; }
    setSaving(true);
    try {
      const body = { product_id: target.product_id, note, warehouse_id: warehouseId || undefined };
      if (mode === "set") body.set_qty = Number(value); else body.delta = Number(value);
      await api.post("/samples/adjust", body);
      toast.success(t("samples.adjusted"));
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="sample-adjust-dialog">
        <DialogHeader><DialogTitle className="text-base">{t("samples.adjust")} · <span className="font-mono">{target.item_no}</span></DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">{t("samples.current")}: <SampleQtyBadge qty={target.sample_qty} /></p>
        <div className="flex gap-2 text-xs">
          {["delta", "set"].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`sample-adjust-mode-${m}`} className={`px-3 py-1.5 rounded-md border ${mode === m ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600"}`}>{t(`samples.mode_${m}`)}</button>
          ))}
        </div>
        <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={mode === "set" ? t("samples.new_qty") : "+5 / -2"} className="h-9 font-mono" data-testid="sample-adjust-value" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("samples.note_placeholder")} className="h-9" data-testid="sample-adjust-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving || value === ""} data-testid="sample-adjust-submit">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter 274: 出入库记录改为弹窗,打开时才加载。
function MovementsDialog({ open, onClose, t, warehouseId }) {
  const [mv, setMv] = useState({ items: [], total: 0 });
  const [mvFilter, setMvFilter] = useState({ type: "", date_from: "", date_to: "", item: "" });
  const loadMovements = useCallback(async () => {
    const params = { warehouse_id: warehouseId || undefined, type: mvFilter.type || undefined, date_from: mvFilter.date_from || undefined, date_to: mvFilter.date_to || undefined, page_size: 200 };
    const { data } = await api.get("/samples/movements", { params });
    setMv(mvFilter.item ? { ...data, items: data.items.filter((m) => (m.item_no || "").toLowerCase().includes(mvFilter.item.toLowerCase())) } : data);
  }, [mvFilter, warehouseId]);
  useEffect(() => { if (open) loadMovements(); }, [open, loadMovements]);

  const exportXlsx = async () => {
    const params = new URLSearchParams(Object.fromEntries(Object.entries({ warehouse_id: warehouseId, type: mvFilter.type, date_from: mvFilter.date_from, date_to: mvFilter.date_to }).filter(([, v]) => v)));
    const res = await api.get(`/samples/movements/export?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "sample_movements.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="samples-movements-dialog">
        <DialogHeader><DialogTitle className="text-base">{t("samples.movements")} · {mv.total}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={mvFilter.item} onChange={(e) => setMvFilter((f) => ({ ...f, item: e.target.value }))} placeholder={t("samples.item_no")} className="h-8 w-36 font-mono text-xs" data-testid="samples-mv-item" />
          <select value={mvFilter.type} onChange={(e) => setMvFilter((f) => ({ ...f, type: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white" data-testid="samples-mv-type">
            <option value="">{t("samples.all_types")}</option>
            {TYPE_KEYS.map((k) => <option key={k} value={k}>{t(`samples.type_${k}`)}</option>)}
          </select>
          <input type="date" value={mvFilter.date_from} onChange={(e) => setMvFilter((f) => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2" data-testid="samples-mv-from" />
          <input type="date" value={mvFilter.date_to} onChange={(e) => setMvFilter((f) => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2" data-testid="samples-mv-to" />
          <Button variant="outline" size="sm" className="h-8 text-xs ml-auto" onClick={exportXlsx} data-testid="samples-mv-export"><Download size={12} className="mr-1" /> Excel</Button>
        </div>
        <div className="bg-white border border-gray-200 overflow-auto max-h-[60vh]">
          <table className="w-full" data-testid="samples-movements-table">
            <thead className="bg-gray-50 sticky top-0"><tr><Th>{t("samples.time")}</Th><Th>{t("warehouse.title_short")}</Th><Th>{t("samples.product")}</Th><Th right>{t("samples.change")}</Th><Th right>{t("samples.balance")}</Th><Th>{t("samples.type")}</Th><Th>{t("samples.quotation")}</Th><Th>{t("samples.note")}</Th><Th>{t("samples.user")}</Th></tr></thead>
            <tbody>
              {mv.items.length === 0 && <tr><td colSpan={9} className="text-center text-xs text-gray-400 py-8">{t("samples.empty")}</td></tr>}
              {mv.items.map((m) => (
                <tr key={m.id} className="border-t border-gray-100" data-testid={`samples-mv-row-${m.id}`}>
                  <Td cls="font-mono text-[11px] text-gray-500 whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</Td>
                  <Td cls="text-[11px] text-gray-600 whitespace-nowrap">{m.warehouse_name}</Td>
                  <Td><span className="font-mono">{m.item_no}</span> <span className="text-gray-400">{m.description}</span></Td>
                  <Td cls={`text-right font-mono font-semibold ${m.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{m.delta > 0 ? `+${m.delta}` : m.delta}</Td>
                  <Td cls="text-right font-mono">{m.balance_after}</Td>
                  <Td>{t(`samples.type_${m.type}`)}</Td>
                  <Td>{m.cart_name}</Td>
                  <Td>{m.note}</Td>
                  <Td>{m.user_name}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SamplesPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("");
  const [stock, setStock] = useState({ items: [], total: 0 });
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [receiveCode, setReceiveCode] = useState("");
  const [receiveQty, setReceiveQty] = useState(1);
  const [mvOpen, setMvOpen] = useState(false);
  // iter 275: admins pick a warehouse (remembered); managers are pinned.
  const { user } = useAuth();
  const { warehouses, selectable, nameOf, reload: reloadWarehouses } = useWarehouses();
  const isMgr = user?.role === "sample_warehouse";
  const [pickedWh, setPickedWh] = useState(() => localStorage.getItem("samplesWarehouseId") || "");
  const warehouseId = isMgr ? (user?.warehouse_id || "") : (pickedWh || selectable[0]?.id || warehouses[0]?.id || "");
  const warehouse = warehouses.find((w) => w.id === warehouseId);
  const [migrateTarget, setMigrateTarget] = useState("");
  const pickWarehouse = (id) => { setPickedWh(id); localStorage.setItem("samplesWarehouseId", id); };

  const loadStock = useCallback(async () => {
    if (!warehouseId) return;
    const { data } = await api.get("/samples/stock", { params: { warehouse_id: warehouseId, q: q || undefined, only: only || undefined, page_size: 100 } });
    setStock(data);
  }, [q, only, warehouseId]);
  useEffect(() => { loadStock(); }, [loadStock]);
  const refresh = () => { loadStock(); };

  const migrate = async () => {
    if (!migrateTarget) return;
    if (!window.confirm(t("warehouse.migrate_confirm", { name: nameOf(migrateTarget) }))) return;
    try {
      const { data } = await api.post(`/sample-warehouses/${migrateTarget}/absorb-unassigned`);
      toast.success(t("warehouse.migrated", { products: data.products, movements: data.movements }));
      await reloadWarehouses(); pickWarehouse(migrateTarget);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
  };

  const receive = async () => {
    if (!receiveCode.trim()) return;
    try {
      const { data: p } = await api.get("/samples/lookup", { params: { code: receiveCode.trim(), warehouse_id: warehouseId } });
      const { data } = await api.post("/samples/receive", { product_id: p.product_id, qty: Number(receiveQty) || 1, note: "manual", warehouse_id: warehouseId });
      toast.success(`${data.item_no}: ${data.sample_qty}`);
      setReceiveCode(""); setReceiveQty(1); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
  };

  return (
    <AppLayout>
    <div className="space-y-8 max-w-6xl" data-testid="samples-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">{t("samples.title")}{isMgr && <WarehouseTag name={nameOf(warehouseId)} testId="samples-warehouse-tag" className="text-xs px-2 py-0.5" />}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("samples.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isMgr && (
            <select value={warehouseId} onChange={(e) => pickWarehouse(e.target.value)} data-testid="samples-warehouse-select"
              className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white font-medium">
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.is_default ? ` (${t("warehouse.default_badge")})` : ""}{w.active ? "" : ` (${t("warehouse.inactive")})`}</option>)}
            </select>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setMvOpen(true)} data-testid="samples-movements-open"><History size={14} className="mr-1.5" />{t("samples.movements")}</Button>
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="samples-refresh"><RefreshCw size={14} /></Button>
        </div>
      </div>

      {!isMgr && warehouse?.is_default && (
        <section className="bg-amber-50 border border-amber-200 p-4 flex flex-wrap items-center gap-3" data-testid="samples-migrate-section">
          <p className="text-xs text-amber-800 mr-auto">{t("warehouse.migrate_hint", { count: stock.total })}</p>
          <select value={migrateTarget} onChange={(e) => setMigrateTarget(e.target.value)} data-testid="samples-migrate-target" className="h-8 text-xs border border-amber-300 rounded-md px-2 bg-white">
            <option value="">{t("warehouse.select")}</option>
            {selectable.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <Button size="sm" className="h-8 text-xs" onClick={migrate} disabled={!migrateTarget || stock.total === 0} data-testid="samples-migrate-button"><ArrowRightLeft size={12} className="mr-1" />{t("warehouse.migrate")}</Button>
        </section>
      )}

      <section className="bg-white border border-gray-200 p-4 space-y-3" data-testid="samples-receive-section">
        <p className="text-xs font-semibold uppercase tracking-label text-gray-500">{t("samples.manual_receive")}</p>
        <div className="flex items-center gap-2">
          <Input value={receiveCode} onChange={(e) => setReceiveCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && receive()} placeholder={t("samples.code_placeholder")} className="h-8 w-56 font-mono text-xs" data-testid="samples-receive-code" />
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setReceiveQty((n) => Math.max(1, Number(n) - 1))}><Minus size={12} /></Button>
            <Input type="number" min="1" value={receiveQty} onChange={(e) => setReceiveQty(e.target.value)} className="h-8 w-16 text-center font-mono text-xs" data-testid="samples-receive-qty" />
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setReceiveQty((n) => Number(n) + 1)}><Plus size={12} /></Button>
          </div>
          <Button size="sm" className="h-8" onClick={receive} data-testid="samples-receive-submit">{t("samples.receive")}</Button>
        </div>
      </section>

      <section className="space-y-3" data-testid="samples-stock-section">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mr-auto">{t("samples.stock")} · {stock.total}</p>
          <div className="relative"><Search size={12} className="absolute left-2 top-2.5 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="h-8 pl-7 w-52 text-xs" data-testid="samples-stock-search" /></div>
          {["", "positive", "negative"].map((o) => (
            <button key={o} type="button" onClick={() => setOnly(o)} data-testid={`samples-stock-only-${o || "all"}`} className={`text-xs px-2.5 py-1.5 rounded-md border ${only === o ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600"}`}>{t(`samples.only_${o || "all"}`)}</button>
          ))}
        </div>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="samples-stock-table">
            <thead className="bg-gray-50"><tr><Th>{t("samples.product")}</Th><Th>{t("samples.customer")}</Th><Th right>{t("samples.qty")}</Th><Th>{t("samples.updated")}</Th><Th right></Th></tr></thead>
            <tbody>
              {stock.items.length === 0 && <tr><td colSpan={5} className="text-center text-xs text-gray-400 py-8">{t("samples.empty")}</td></tr>}
              {stock.items.map((p) => (
                <tr key={p.product_id} className="border-t border-gray-100" data-testid={`samples-stock-row-${p.product_id}`}>
                  <Td><div className="flex items-center gap-2">{p.photo_url ? <img src={p.photo_url} alt="" className="w-9 h-9 object-cover rounded border border-gray-100" /> : <div className="w-9 h-9 bg-gray-50 rounded" />}<div><p className="font-mono text-xs text-gray-900">{p.item_no}</p><p className="text-[11px] text-gray-500 truncate max-w-xs">{p.description}</p></div></div></Td>
                  <Td>{p.customer_name}</Td>
                  <Td cls="text-right"><SampleQtyBadge qty={p.sample_qty} testId={`samples-stock-qty-${p.product_id}`} /></Td>
                  <Td cls="font-mono text-[11px] text-gray-500">{p.sample_updated_at ? new Date(p.sample_updated_at).toLocaleString() : "-"}</Td>
                  <Td cls="text-right"><Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAdjustTarget(p)} data-testid={`samples-adjust-${p.product_id}`}>{t("samples.adjust")}</Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AdjustDialog target={adjustTarget} onClose={() => setAdjustTarget(null)} onDone={refresh} t={t} warehouseId={warehouseId} />
      <MovementsDialog open={mvOpen} onClose={() => setMvOpen(false)} t={t} warehouseId={warehouseId} />
    </div>
    </AppLayout>
  );
}
